import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import WhatsAppButton from "@/components/public/WhatsAppButton";
import { Link } from "wouter";
import { BRAND } from "@/brand";
import { useSeo } from "@/hooks/useSeo";

export default function LegalPage() {
  useSeo({ title: "Aviso legal | Golf Motion Lab" });
  const lastUpdated = "29 de mayo de 2026";

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <PublicHeader />

      <section className="container py-20 md:py-32 max-w-3xl">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
          Información legal
        </div>
        <h1
          className="font-display font-light text-[var(--forest-deep)] leading-[1.05] mb-6"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
        >
          Aviso legal y condiciones de uso
        </h1>
        <p className="text-sm text-[var(--forest-deep)]/60 mb-12">
          Última actualización: {lastUpdated}
        </p>

        <div className="prose prose-neutral max-w-none text-[var(--forest-deep)]/85 leading-relaxed">
          <p>
            El presente Aviso Legal regula el acceso, navegación y uso del sitio web{" "}
            <strong>{BRAND.domain}</strong> (en adelante, el “Sitio Web”), titularidad de{" "}
            <strong>{BRAND.name}</strong>. El simple acceso al Sitio Web atribuye a quien lo
            realiza la condición de Usuario e implica la aceptación plena y sin reservas de todas
            las disposiciones incluidas en este Aviso Legal, así como en la{" "}
            <Link href="/privacidad" className="text-[var(--forest)] underline">
              Política de Privacidad
            </Link>{" "}
            y la{" "}
            <Link href="/cookies" className="text-[var(--forest)] underline">
              Política de Cookies
            </Link>
            .
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            1. Datos identificativos del titular
          </h2>
          <p>
            En cumplimiento del deber de información recogido en el artículo 10 de la Ley 34/2002,
            de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio
            Electrónico (LSSI-CE), se facilitan a continuación los datos identificativos del
            titular del Sitio Web:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Titular:</strong> Sergio Sánchez Marín
            </li>
            <li>
              <strong>NIF:</strong> 47398641B
            </li>
            <li>
              <strong>Domicilio:</strong> Calle Arquitecto Fernández nº 52, 4º A · 02005 Albacete (Albacete), España
            </li>
            <li>
              <strong>Nombre comercial:</strong> {BRAND.name}
            </li>
            <li>
              <strong>Correo electrónico de contacto:</strong>{" "}
              <a href={`mailto:${BRAND.email}`} className="text-[var(--forest)] underline">
                {BRAND.email}
              </a>
            </li>
            <li>
              <strong>Teléfono / WhatsApp:</strong> {BRAND.whatsappDisplay}
            </li>
            <li>
              <strong>Sitio web:</strong> https://{BRAND.domain}
            </li>
          </ul>
          <p className="text-sm text-[var(--forest-deep)]/70 mt-3">
            La actividad se desarrolla actualmente como persona física. {BRAND.name} se
            utiliza como nombre comercial hasta la formalización de la sociedad mercantil,
            momento en el que estos datos identificativos serán actualizados.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            2. Objeto y actividad
          </h2>
          <p>
            {BRAND.name} es un laboratorio de valoración integral del golfista que combina
            análisis biomecánico del swing, evaluación de la fuerza y movilidad, tecnología de
            medición (TrackMan, VALD ForceFrame, OnForm) y valoración del control motor. A través
            del Sitio Web se ofrece información sobre la metodología, el equipo profesional, los
            servicios de valoración y los canales de contacto. El Sitio Web no comercializa
            productos sanitarios ni emite diagnósticos clínicos a través de internet; cualquier
            proceso de valoración se realiza presencialmente con los profesionales del equipo.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            3. Condiciones de uso
          </h2>
          <p>
            El Usuario se compromete a utilizar el Sitio Web, sus contenidos y servicios de forma
            diligente y conforme a la ley, la moral, el orden público y al presente Aviso Legal.
            Queda prohibido cualquier uso del Sitio Web con fines ilícitos o que dañe, sobrecargue
            o impida el normal funcionamiento del mismo o de los sistemas informáticos de{" "}
            {BRAND.name} o de terceros.
          </p>
          <p>
            El Usuario se abstendrá, en particular, de introducir o difundir contenidos o
            propaganda de carácter racista, xenófobo, pornográfico, de apología del terrorismo o
            que atenten contra los derechos humanos, así como de introducir programas, virus,
            macros u otra secuencia de caracteres que pueda producir alteraciones en los sistemas
            informáticos de {BRAND.name} o de terceros.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            4. Propiedad intelectual e industrial
          </h2>
          <p>
            Todos los contenidos del Sitio Web (textos, fotografías, gráficos, imágenes, iconos,
            tecnología, software, vídeos, animaciones, grabaciones, así como el diseño gráfico y
            códigos fuente) son titularidad de {BRAND.name} o, en su caso, cuenta con la
            autorización expresa de sus legítimos propietarios. Las marcas, nombres comerciales y
            logotipos que aparecen son propiedad de {BRAND.name} o de los terceros que han
            autorizado su uso.
          </p>
          <p>
            Tecnologías como TrackMan, VALD ForceFrame u OnForm son marcas registradas de sus
            respectivos titulares y se mencionan únicamente con fines descriptivos e
            informativos. Su mención no implica vinculación contractual ni patrocinio entre
            dichas marcas y {BRAND.name}, salvo que se indique expresamente.
          </p>
          <p>
            Queda expresamente prohibida la reproducción, distribución, comunicación pública,
            transformación o cualquier otra forma de explotación, total o parcial, de los
            contenidos del Sitio Web sin la autorización previa y por escrito de {BRAND.name}.
            Cualquier uso no autorizado será considerado una infracción grave de los derechos de
            propiedad intelectual o industrial.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            5. Enlaces a sitios de terceros
          </h2>
          <p>
            El Sitio Web puede contener enlaces a páginas web o servicios de terceros (por
            ejemplo, WhatsApp, redes sociales o fabricantes de tecnología). {BRAND.name} no se
            responsabiliza de los contenidos, políticas de privacidad ni de las prácticas de
            dichos sitios, cuyo uso queda sujeto a sus propios términos y condiciones.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            6. Exclusión de garantías y responsabilidad
          </h2>
          <p>
            {BRAND.name} no garantiza la disponibilidad y continuidad ininterrumpidas del Sitio
            Web. En la medida permitida por la ley, queda exonerada de cualquier responsabilidad
            por los daños y perjuicios de cualquier naturaleza que puedan deberse a la falta de
            disponibilidad o de continuidad del funcionamiento del Sitio Web, a la defraudación
            de la utilidad esperada o a la existencia de virus u otros componentes lesivos en el
            sistema desde el cual el Usuario accede.
          </p>
          <p>
            La información publicada en el Sitio Web tiene carácter divulgativo y orientativo. En
            ningún caso sustituye el consejo de un profesional sanitario o el resultado de una
            valoración presencial realizada por el equipo clínico y técnico de {BRAND.name}.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            7. Modificaciones
          </h2>
          <p>
            {BRAND.name} se reserva el derecho a efectuar, sin previo aviso, las modificaciones
            que considere oportunas en el Sitio Web, pudiendo cambiar, suprimir o añadir tanto
            los contenidos y servicios que se presten, como la forma en la que estos aparezcan
            presentados o localizados, así como las condiciones recogidas en el presente Aviso
            Legal.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            8. Legislación aplicable y jurisdicción
          </h2>
          <p>
            El presente Aviso Legal se rige por la legislación española. Para cualquier
            controversia que pudiera derivarse del acceso o uso del Sitio Web, las partes se
            someten a los Juzgados y Tribunales del domicilio del titular, salvo cuando la
            normativa aplicable, en especial la de consumidores y usuarios, establezca un fuero
            distinto.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            9. Contacto
          </h2>
          <p>
            Para cualquier consulta sobre este Aviso Legal puedes escribir a{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[var(--forest)] underline">
              {BRAND.email}
            </a>
            .
          </p>

          <p className="text-sm text-[var(--forest-deep)]/60 italic mt-16">
            Este texto se ha redactado tomando como referencia la LSSI-CE, el RGPD y la LOPDGDD.
            Antes de su publicación definitiva deberá ser revisado por un asesor legal y
            actualizado con los datos de la sociedad mercantil una vez se constituya.
          </p>
        </div>
      </section>

      <PublicFooter />
      <WhatsAppButton />
    </div>
  );
}
